// Checklisten-Vorlagen für Tickets (Tabelle ticket_checklisten_vorlagen):
// Vorlagen enthalten den Ablauf als ☐-Zeilen plus optionale Abschnitts-Überschriften —
// beim Einfügen wird die Liste in die Ticket-Beschreibung KOPIERT (Vorlage bleibt
// unverändert). Abgehakt wird direkt in der Beschreibung (☐ ↔ ☑, TicketVerlaufThread).
//
// Format-Konvention (stabil unter mehrfacher Normalisierung):
//  - "1. Abschnitt" / "2) Abschnitt"  → Überschrift (kein Kästchen, fett gerendert)
//  - "# Abschnitt"                    → Überschrift ohne Nummer (# bleibt gespeichert,
//                                       das Rendering blendet es aus)
//  - alles andere                     → ☐-Punkt; Leerzeilen strukturieren (max. eine,
//                                       vor Überschriften automatisch)
import { supabase } from '../supabase/client';

export interface ChecklistenVorlage {
  id: string;
  name: string;
  checkliste: string;
  sortierung: number;
  aktiv: boolean;
}

const NUMMERIERT = /^\d+[.)]\s/;

/** Überschriften-Zeile im gespeicherten Format? ("1. …" / "2) …" / "# …") */
export function istChecklistenUeberschrift(zeile: string): boolean {
  return NUMMERIERT.test(zeile) || zeile.startsWith('# ');
}

/**
 * Punkte bekommen ein ☐-Präfix (☐/☑ bleiben erhalten), Überschriften bleiben ohne
 * Kästchen (ein versehentliches "☐ 1. …" wird zur Überschrift zurückgebaut) und
 * erhalten automatisch eine Leerzeile davor; mehrfache Leerzeilen werden reduziert.
 */
export function normalisiereCheckliste(text: string): string {
  const ergebnis: string[] = [];
  for (const roh of text.split('\n')) {
    const z = roh.trim();
    if (!z) {
      if (ergebnis.length && ergebnis[ergebnis.length - 1] !== '') ergebnis.push('');
      continue;
    }
    const ohneKasten = z.startsWith('☐ ') ? z.slice(2).trim() : z;
    const istUeberschrift = !z.startsWith('☑ ') && istChecklistenUeberschrift(ohneKasten);
    if (istUeberschrift) {
      if (ergebnis.length && ergebnis[ergebnis.length - 1] !== '') ergebnis.push('');
      ergebnis.push(ohneKasten);
      continue;
    }
    ergebnis.push(z.startsWith('☐ ') || z.startsWith('☑ ') ? z : `☐ ${z}`);
  }
  while (ergebnis.length && ergebnis[ergebnis.length - 1] === '') ergebnis.pop();
  return ergebnis.join('\n');
}

/** Kopiert die Checkliste ans Ende der Beschreibung (mit Leerzeile als Trenner). */
export function fuegeChecklisteAn(beschreibung: string, checkliste: string): string {
  const liste = normalisiereCheckliste(checkliste);
  if (!liste) return beschreibung;
  const basis = beschreibung.trimEnd();
  return basis.trim() ? `${basis}\n\n${liste}` : liste;
}

/** Fortschritt über alle Checkbox-Zeilen eines Texts (für die n/m-Anzeige). */
export function zaehleChecklistenFortschritt(text: string | null | undefined): { erledigt: number; gesamt: number } {
  const zeilen = (text ?? '').split('\n');
  const erledigt = zeilen.filter((z) => z.startsWith('☑ ')).length;
  const offen = zeilen.filter((z) => z.startsWith('☐ ')).length;
  return { erledigt, gesamt: erledigt + offen };
}

// ── DB-Wrapper (dünn, Konvention: nicht unit-getestet) ─────────────────────────

export async function ladeChecklistenVorlagen(nurAktive = true): Promise<ChecklistenVorlage[]> {
  let q = supabase
    .from('ticket_checklisten_vorlagen')
    .select('id, name, checkliste, sortierung, aktiv')
    .order('sortierung', { ascending: true })
    .order('name', { ascending: true });
  if (nurAktive) q = q.eq('aktiv', true);
  const { data, error } = await q;
  if (error) throw error;
  return (data as ChecklistenVorlage[]) || [];
}
